import { useSelector } from 'react-redux'
import { useState } from 'react'

import { ReactComponent as CleverIcon } from '@/assets/icons/clever-icon-rounded.svg'
import { ReactComponent as GoogleClassroomIcon } from '@/assets/icons/google-icon-rounded.svg'
import { ReactComponent as CanvasIcon } from '@/assets/icons/canvas-icon-rounded.svg'
import { ReactComponent as MoodleIcon } from '@/assets/icons/moodle-icon-rounded.svg'
import { ReactComponent as BlackboardIcon } from '@/assets/icons/blackboard-icon-rounded.svg'
import { ReactComponent as SchoologyIcon } from '@/assets/icons/schoology-icon-rounded.svg'
import { ReactComponent as BrightspaceIcon } from '@/assets/icons/brightspace-rounded.svg'
import useGoogleClassroomAuth from '@/hooks/useGoogleClassroomAuth'
import { useAuthUser, useUser } from '@/hooks/useUser'

export function useIntegrationsData() {
  const { auth, profile } = useUser()
  const isGoogleUser = profile?.isGoogleUser
  const isConnected = profile?.connectedGoogleClassroom
  const [loading, setLoading] = useState(false)
  const { initiateAuth } = useGoogleClassroomAuth()
  const isCleverConnected = auth?.custom_claims?.clever_id || profile?.cleverId
  const isTeacher = profile?.isTeacher || false

  // Connect functions

  const handleGoogleClassroomConnect = async () => {
    setLoading(true)
    try {
      await initiateAuth()
    } catch (e) {
      // handle error
    } finally {
      setLoading(false)
    }
  }

  const handleCleverConnect = async () => {
    window.open('https://clever.com/library/app/speakable', '_blank')
  }

  // Learn More functions

  const handleGoogleLearnMore = () => {
    window.open('https://www.speakable.io/integrations/google', '_blank')
  }

  const handleCleverLearnMore = () => {
    window.open('https://www.speakable.io/integrations/clever', '_blank')
  }

  const handleCanvasLearnMore = () => {
    window.open('https://www.speakable.io/integrations/canvas', '_blank')
  }

  const handleMoodleLearnMore = () => {
    window.open('https://www.speakable.io/integrations/moodle', '_blank')
  }

  const handleBlackboardLearnMore = () => {
    window.open('https://www.speakable.io/integrations/blackboard', '_blank')
  }

  const handleSchoologyLearnMore = () => {
    window.open('https://www.speakable.io/integrations/schoology', '_blank')
  }
  const handleBrightspaceLearnMore = () => {
    window.open('https://www.speakable.io/integrations/brightspace', '_blank')
  }

  const freeIntegrations = [
    {
      id: 'google-classroom',
      title: 'Google Classroom',
      description: 'Classroom sync and gradebook integration',
      icon: GoogleClassroomIcon,
      connected: isConnected,
      loading: loading,
      onConnect: handleGoogleClassroomConnect,
      onLearnMore: handleGoogleLearnMore,
      learnMoreLabel: 'Learn more',
      visible: true,
      disabled: !isGoogleUser,
      tooltipContent: 'Login through Google to connect',
    },
    {
      id: 'clever',
      title: 'Clever',
      description: 'Clever Library and roster sync',
      icon: CleverIcon,
      connected: isCleverConnected,
      loading: false,
      onConnect: handleCleverConnect,
      onLearnMore: handleCleverLearnMore,
      learnMoreLabel: 'Learn more',
      visible: isTeacher,
      disabled: !isCleverConnected,
      tooltipContent: 'Login through the Clever Library to connect',
    },
  ]
  const limitedIntegrations = [
    {
      id: 'canvas',
      title: 'Canvas',
      // description: 'Integrates tools for seamless learning.',
      icon: CanvasIcon,
      connected: false,
      loading: false,
      onConnect: null,
      onLearnMore: handleCanvasLearnMore,
      learnMoreLabel: 'Learn more',
      visible: isTeacher,
    },
    {
      id: 'moodle',
      title: 'Moodle',
      // description: 'Integrates tools for seamless learning.',
      icon: MoodleIcon,
      connected: false,
      loading: false,
      onConnect: null,
      onLearnMore: handleMoodleLearnMore,
      learnMoreLabel: 'Learn more',
      visible: isTeacher,
    },
    {
      id: 'blackboard',
      title: 'Blackboard',
      // description: 'Integrates tools for seamless learning.',
      icon: BlackboardIcon,
      connected: false,
      loading: false,
      onConnect: null,
      onLearnMore: handleBlackboardLearnMore,
      learnMoreLabel: 'Learn more',
      visible: isTeacher,
    },
    {
      id: 'schoology',
      title: 'Schoology',
      // description: 'Integrates tools for seamless learning.',
      icon: SchoologyIcon,
      connected: false,
      loading: false,
      onConnect: null,
      onLearnMore: handleSchoologyLearnMore,
      learnMoreLabel: 'Learn more',
      visible: isTeacher,
    },
    {
      id: 'brightspace',
      title: 'Brightspace',
      // description: 'Integrates tools for seamless learning.',
      icon: BrightspaceIcon,
      connected: false,
      loading: false,
      onConnect: null,
      onLearnMore: handleBrightspaceLearnMore,
      learnMoreLabel: 'Learn more',
      visible: isTeacher,
    },
  ]

  return { freeIntegrations, limitedIntegrations }
}
